import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import CustomerLayout from '../components/CustomerLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Star, Globe, Package, Plane, ShoppingCart } from 'lucide-react';
import { formatPrice } from '../lib/utils';

// Traditional decorative elements
const FloralCorner = () => (
  <svg viewBox="0 0 50 50" className="w-full h-full" fill="currentColor">
    <circle cx="10" cy="10" r="3" />
    <path d="M10 5 Q15 10 10 15 Q5 10 10 5" />
    <path d="M5 10 Q10 5 15 10 Q10 15 5 10" />
    <circle cx="25" cy="5" r="2" />
    <circle cx="5" cy="25" r="2" />
  </svg>
);

const PaisleySmall = () => (
  <svg viewBox="0 0 20 30" className="w-full h-full" fill="currentColor">
    <path d="M10 2 Q18 8 18 18 Q18 28 10 28 Q5 28 5 20 Q5 12 10 8 Q12 10 12 15 Q12 22 8 22 Q6 22 6 18 Q6 14 10 12" fillRule="evenodd" />
  </svg>
);

export default function InternationalPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchInternationalProducts();
  }, []);

  const fetchInternationalProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/products/international`);
      setProducts(response.data.map(p => ({...p, id: p.id || p._id})));
    } catch (error) {
      console.error('Failed to fetch international products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomerLayout>
      <div className="min-h-screen bg-[#FAFDF8]">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-[#1E3A5F] via-[#2C5282] to-[#1E3A5F] text-white py-16 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-4 left-4 w-12 h-12 text-white/10">
              <FloralCorner />
            </div>
            <div className="absolute top-4 right-4 w-12 h-12 text-white/10 rotate-90">
              <FloralCorner />
            </div>
            <div className="absolute bottom-4 left-4 w-12 h-12 text-white/10 -rotate-90">
              <FloralCorner />
            </div>
            <div className="absolute bottom-4 right-4 w-12 h-12 text-white/10 rotate-180">
              <FloralCorner />
            </div>
            <div className="absolute top-1/2 left-10 -translate-y-1/2 w-8 h-12 text-white/5">
              <PaisleySmall />
            </div>
            <div className="absolute top-1/2 right-10 -translate-y-1/2 w-8 h-12 text-white/5 rotate-180">
              <PaisleySmall />
            </div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 relative">
            <div className="text-center">
              <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Globe className="w-5 h-5 mr-2" />
                <span className="font-bold">Worldwide Shipping</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 flex items-center justify-center gap-4">
                <Globe className="w-12 h-12" />
                International Delivery
              </h1>
              <p className="text-lg text-blue-100 max-w-2xl mx-auto">
                Authentic Indian products delivered worldwide. Experience the taste of India, no matter where you are!
              </p>
            </div>
          </div>
        </div>

        {/* Info Cards */}
        <div className="max-w-7xl mx-auto px-4 -mt-8 mb-12 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-2 border-[#1E3A5F]/20 shadow-xl bg-white text-center">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-[#1E3A5F]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Plane className="w-7 h-7 text-[#1E3A5F]" />
                </div>
                <h3 className="font-bold text-lg text-[#1E3A5F] mb-2">Global Shipping</h3>
                <p className="text-sm text-[#1E3A5F]/70">We deliver to major countries worldwide</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-[#1E3A5F]/20 shadow-xl bg-white text-center">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-[#1E3A5F]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="w-7 h-7 text-[#1E3A5F]" />
                </div>
                <h3 className="font-bold text-lg text-[#1E3A5F] mb-2">Secure Packaging</h3>
                <p className="text-sm text-[#1E3A5F]/70">Products packed carefully for long-distance travel</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-[#1E3A5F]/20 shadow-xl bg-white text-center">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-[#1E3A5F]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-7 h-7 text-[#1E3A5F]" />
                </div>
                <h3 className="font-bold text-lg text-[#1E3A5F] mb-2">Authentic Products</h3>
                <p className="text-sm text-[#1E3A5F]/70">Genuine regional specialties from India</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Products Section */}
        <div className="max-w-7xl mx-auto px-4 pb-16">
          <h2 className="text-2xl font-bold text-[#1E3A5F] mb-8 text-center">
            Available for International Shipping ({products.length} Products)
          </h2>

          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border-4 border-[#1E3A5F]/20 border-t-[#1E3A5F] rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-[#1E3A5F]/70">Loading products...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-[#1E3A5F]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-10 h-10 text-[#1E3A5F]/50" />
              </div>
              <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">No International Products Yet</h3>
              <p className="text-[#1E3A5F]/70 mb-6">Check back soon for products available for international delivery!</p>
              <Link to="/products" className="inline-block bg-[#1E3A5F] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#2C5282] transition-colors">
                Browse All Products
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product) => (
                <Link
                  key={product.id}
                  to={`/products/${product.slug || product.id}`}
                  className="group"
                >
                  <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 border-[#1E3A5F]/20 hover:border-[#1E3A5F]/50 bg-white h-full">
                    <div className="relative aspect-square overflow-hidden">
                      <img 
                        src={product.images?.[0] || 'https://via.placeholder.com/400x400/E8F5E9/2E7D32?text=No+Image'} 
                        alt={product.name} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                      />
                      <Badge className="absolute top-3 right-3 bg-[#1E3A5F] text-white font-semibold flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        <span>Ships Worldwide</span>
                      </Badge>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-bold text-[#1E3A5F] mb-2 line-clamp-2 group-hover:text-[#2C5282]">{product.name}</h3>
                      <p className="text-xs text-[#1E3A5F]/70 mb-3 line-clamp-2">{product.description}</p>
                      {product.region_name && (
                        <p className="text-xs text-[#2C5282] mb-2">📍 {product.region_name}</p>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-xl font-bold text-[#0F5132]">
                          {formatPrice(product.sale_price || product.price)}
                        </span>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-[#FFD700] fill-current" />
                          <span className="text-xs text-[#1E3A5F]/70">4.5</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2C5282] text-white py-12 relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-4 left-4 w-10 h-10 text-white/10">
              <FloralCorner />
            </div>
            <div className="absolute bottom-4 right-4 w-10 h-10 text-white/10 rotate-180">
              <FloralCorner />
            </div>
          </div>
          <div className="max-w-4xl mx-auto px-4 text-center relative">
            <h2 className="text-2xl font-bold mb-4">Have Questions About International Shipping?</h2>
            <p className="text-blue-100 mb-6">Contact us for shipping rates and delivery times to your country</p>
            <Link
              to="/contact"
              className="inline-block bg-white text-[#1E3A5F] font-bold px-8 py-3 rounded-full hover:bg-blue-50 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
}
